// Phát hiện phiên Claude Code đang mở trong MỘT workspace — port từ extension
// (`src/extension/src/lib/detect-open-claude-code-session.ts`) cho `avatar sync --all` (#211).
//
// Vì sao dùng transcript chứ không phải tiến trình: `pgrep claude` trên máy dev bắt cả
// Claude Desktop (đo 2026-08-17: 25 tiến trình, `cwd` đều là `/`) ⇒ không phân biệt được
// workspace. Thư mục transcript gắn thẳng với đường dẫn workspace nên tín hiệu đúng chỗ.
//
// Ba trạng thái, không phải hai: "chắc chắn không có phiên" khác hẳn "không đo được" —
// gộp lại là im lặng chạy trong lúc đang mù. `unknown` ⇒ caller KHÔNG chặn.
//
// ⚠️ MỘT ĐIỂM KHÁC bản extension, CỐ Ý — không phải "port giữ nguyên": kiểu trả về mang
// thêm `newestMtimeMs`. Bản extension chỉ có `{status, evidence}` và vứt biến cục bộ đó đi,
// nhưng CLI cần nó làm **mốc #2 của luật stale** (`plan-sync-all-across-registered-workspaces.ts`):
// `status` MỘT MÌNH không đủ vì `none` gộp 2 ca khác hẳn nhau — "chưa từng có phiên" (ENOENT)
// với "transcript CŨ". Ca thứ hai chính là bằng chứng workspace còn sống mà luật stale cần.
// Không tách field này ra thì hoặc mất mốc #2, hoặc phải quét thư mục transcript LẦN THỨ HAI.
import { readdirSync, statSync } from "node:fs";
import { homedir } from "node:os";
import { join, resolve } from "node:path";

/**
 * Cửa sổ coi là "phiên còn sống". Rộng có chủ đích: sai dương tốn một dòng in ra
 * (kèm lối thoát `--force-open-sessions <path>`), sai âm làm hỏng phiên đang chạy —
 * `atomicSwapDir` rút `.claude/pack` dưới chân Claude Code.
 */
export const RECENT_ACTIVITY_WINDOW_MS = 30 * 60 * 1000;

const CLAUDE_PROJECTS_SEGMENTS: readonly string[] = [".claude", "projects"];

export type ClaudeSessionStatus = "open" | "none" | "unknown";

export interface ClaudeSessionDetection {
  status: ClaudeSessionStatus;
  /**
   * Chuỗi cho người dùng đọc để tự đánh giá.
   * KHÔNG chứa đường dẫn (đường dẫn home lộ username) hay nội dung file.
   */
  evidence: string;
  /**
   * mtime transcript MỚI NHẤT (ms epoch), hoặc `null` khi không có mốc nào đọc được:
   * ENOENT (chưa từng có phiên), thư mục rỗng / không có `.jsonl` nào, và ca đọc lỗi
   * (`unknown`). Có giá trị KỂ CẢ khi `status === "none"` vì transcript quá cũ — đó
   * đúng là ca luật stale cần.
   *
   * ⚠️ `null` nghĩa là KHÔNG CÓ MỐC, không phải "mốc = 0". Caller phải BỎ nó khỏi phép
   * `max`, đừng quy về 0.
   */
  newestMtimeMs: number | null;
}

/**
 * Claude Code lưu transcript ở `~/.claude/projects/<slug>` với slug là đường dẫn
 * workspace thay mọi dấu phân cách bằng "-".
 *
 * ⚠️ Hai vế dễ rơi khi port, cả hai đều làm hỏng IM LẶNG: `resolve` phải chạy TRƯỚC
 * (path tương đối/`..` ra slug khác ⇒ dò nhầm thư mục), và class ký tự thay CẢ `\` lẫn
 * `/` (chỉ thay `/` ⇒ slug sai trên win32 ⇒ luôn ra `none`, luật open-session chết đúng
 * nền tảng không ai test).
 */
export function workspaceSlugForClaudeProjects(workspaceRoot: string): string {
  return resolve(workspaceRoot).replace(/[\\/]/g, "-");
}

function describeAge(ageMs: number): string {
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return "dưới 1 phút trước";
  return `${String(minutes)} phút trước`;
}

const NO_TRANSCRIPT: ClaudeSessionDetection = {
  status: "none",
  evidence: "Không thấy transcript phiên nào cho workspace này.",
  newestMtimeMs: null,
};

export function detectOpenClaudeCodeSession(
  workspaceRoot: string,
  homeDir: string = homedir(),
  now: number = Date.now(),
): ClaudeSessionDetection {
  const projectDir = join(
    homeDir,
    ...CLAUDE_PROJECTS_SEGMENTS,
    workspaceSlugForClaudeProjects(workspaceRoot),
  );

  let entries: string[];
  try {
    entries = readdirSync(projectDir);
  } catch (error) {
    const code = (error as NodeJS.ErrnoException).code;
    // ENOENT = chưa từng có phiên Claude Code nào ở workspace này — kết luận CHẮC CHẮN.
    if (code === "ENOENT") return NO_TRANSCRIPT;
    return {
      status: "unknown",
      evidence: `Không đọc được thư mục transcript (${code ?? "lỗi không rõ"}).`,
      newestMtimeMs: null,
    };
  }

  let newestMtimeMs = 0;
  for (const entry of entries) {
    if (!entry.endsWith(".jsonl")) continue;
    try {
      const { mtimeMs } = statSync(join(projectDir, entry));
      newestMtimeMs = Math.max(newestMtimeMs, mtimeMs);
    } catch {
      // File vừa bị xoá giữa readdir và stat — bỏ qua, không làm hỏng cả phép dò.
    }
  }

  // 0 = thư mục có thật nhưng không có `.jsonl` nào đọc được ⇒ vẫn là "không có mốc".
  if (newestMtimeMs === 0) return NO_TRANSCRIPT;

  const ageMs = now - newestMtimeMs;
  if (ageMs <= RECENT_ACTIVITY_WINDOW_MS) {
    return {
      status: "open",
      evidence: `Có phiên Claude Code hoạt động ${describeAge(ageMs)} trong workspace này.`,
      newestMtimeMs,
    };
  }
  return {
    status: "none",
    evidence: `Phiên Claude Code gần nhất là ${describeAge(ageMs)} — coi như đã đóng.`,
    newestMtimeMs,
  };
}
